/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {Attribute, Directive, forwardRef} from '@angular/core';

import {NumberWrapper} from '../../facade/lang';
import {AbstractControl} from '../model';
import {NG_VALIDATORS, Validators} from '../validators';



/**
 * An interface that can be implemented by classes that can act as validators.
 *
 * ## Usage
 *
 * ```typescript
 * @Directive({
 *   selector: '[custom-validator]',
 *   providers: [{provide: NG_VALIDATORS, useExisting: CustomValidatorDirective, multi: true}]
 * })
 * class CustomValidatorDirective implements Validator {
 *   validate(c: Control): {[key: string]: any} {
 *     return {"custom": true};
 *   }
 * }
 * ```
 *
 * @experimental
 */
export interface Validator { validate(c: AbstractControl): {[key: string]: any}; }

const REQUIRED = /*@ts2dart_const*/ Validators.required;

export const REQUIRED_VALIDATOR: any = /*@ts2dart_const*/ /*@ts2dart_Provider*/ {
  provide: NG_VALIDATORS,
  useValue: REQUIRED,
  multi: true
};
export class RequiredValidator {
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[required][ngControl],[required][ngFormControl],[required][ngModel]',
  providers: [REQUIRED_VALIDATOR]
}, ] },
];
}

export interface ValidatorFn { (c: AbstractControl): {[key: string]: any}; }
export interface AsyncValidatorFn {
  (c: AbstractControl): any /*Promise<{[key: string]: any}>|Observable<{[key: string]: any}>*/;
}

/**
 * Provivder which adds {@link MinLengthValidator} to {@link NG_VALIDATORS}.
 *
 * ## Example:
 *
 * {@example common/forms/ts/validators/validators.ts region='min'}
 */
export const MIN_LENGTH_VALIDATOR: any = /*@ts2dart_const*/ /*@ts2dart_Provider*/ {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MinLengthValidator),
  multi: true
};
export class MinLengthValidator implements Validator {
  private _validator: ValidatorFn;

  constructor( minLength: string) {
    this._validator = Validators.minLength(NumberWrapper.parseInt(minLength, 10));
  }

  validate(c: AbstractControl): {[key: string]: any} { return this._validator(c); }
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[minlength][ngControl],[minlength][ngFormControl],[minlength][ngModel]',
  providers: [MIN_LENGTH_VALIDATOR]
}, ] },
];
/** @nocollapse */
static ctorParameters: {type: Function, decorators?: DecoratorInvocation[]}[] = [
{type: undefined, decorators: [{ type: Attribute, args: ['minlength', ] }, ]},
];
}

/**
 * Provider which adds {@link MaxLengthValidator} to {@link NG_VALIDATORS}.
 *
 * ## Example:
 *
 * {@example common/forms/ts/validators/validators.ts region='max'}
 */
export const MAX_LENGTH_VALIDATOR: any = /*@ts2dart_const*/ /*@ts2dart_Provider*/ {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MaxLengthValidator),
  multi: true
};
export class MaxLengthValidator implements Validator {
  private _validator: ValidatorFn;

  constructor( maxLength: string) {
    this._validator = Validators.maxLength(NumberWrapper.parseInt(maxLength, 10));
  }

  validate(c: AbstractControl): {[key: string]: any} { return this._validator(c); }
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[maxlength][ngControl],[maxlength][ngFormControl],[maxlength][ngModel]',
  providers: [MAX_LENGTH_VALIDATOR]
}, ] },
];
/** @nocollapse */
static ctorParameters: {type: Function, decorators?: DecoratorInvocation[]}[] = [
{type: undefined, decorators: [{ type: Attribute, args: ['maxlength', ] }, ]},
];
}


export const PATTERN_VALIDATOR: any = /*@ts2dart_const*/ /*@ts2dart_Provider*/ {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => PatternValidator),
  multi: true
};
export class PatternValidator implements Validator {
  private _validator: ValidatorFn;

  constructor( pattern: string) {
    this._validator = Validators.pattern(pattern);
  }

  validate(c: AbstractControl): {[key: string]: any} { return this._validator(c); }
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[pattern][ngControl],[pattern][ngFormControl],[pattern][ngModel]',
  providers: [PATTERN_VALIDATOR]
}, ] },
];
/** @nocollapse */
static ctorParameters: {type: Function, decorators?: DecoratorInvocation[]}[] = [
{type: undefined, decorators: [{ type: Attribute, args: ['pattern', ] }, ]},
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
